import { Divider, Flex, IconButton, Tooltip } from '@chakra-ui/react';
import { TABS } from '@llx/common';
import { Fragment, useEffect, useState } from 'react';

import IconChatOff from '@/assets/images/icon_chat_off.svg?react';
import IconUsersOff from '@/assets/images/icon_users_off.svg?react';
import IconVideoOff from '@/assets/images/icon_video_off.svg?react';
import { useLoggedInUserAuthContext } from '@/components/AuthTokenContextProvider/UserAuthContext';
import { useCurrentMeetingDataContext } from '@/features/meeting/context/CurrentMeetingDataContext';
import useConditionalTimeout from '@/hooks/useConditionalTimeout';
import { getUserType } from '@/utils/helpers/getUserType';
import shouldActivateChatFeature from '@/utils/helpers/meeting/shouldActivateChatFeature';
import shouldActivatePIPFeature from '@/utils/helpers/meeting/shouldActivatePIPFeature';
import shouldActivateUserListFeature from '@/utils/helpers/meeting/shouldActivateUserListFeature';

export type MeetingRightSidebarProps = {
  currentTab: TABS;
  onParticipantsClick: () => void;
  onChatClick: () => void;
  onVideoClick: () => void;
  amtUnreadMessages: number;
};

export default function MeetingRightSideBar(props: MeetingRightSidebarProps) {
  const { currentMeeting: meeting, isUserHost } = useCurrentMeetingDataContext();
  const { isGuest, isAdmin } = useLoggedInUserAuthContext();
  const [showUnreadNotification, setShowUnreadNotification] = useState(false);
  const userType = getUserType(isUserHost, isGuest, isAdmin);

  // NOTE: shows the unread messages notification after 500ms
  useConditionalTimeout(
    () => {
      setShowUnreadNotification(true);
    },
    500,
    props.amtUnreadMessages !== 0
  );

  useEffect(() => {
    if (props.amtUnreadMessages === 0) {
      setShowUnreadNotification(false);
    }
  }, [props.amtUnreadMessages]);

  const elements = [
    {
      conditionToShow: shouldActivateUserListFeature(meeting, userType),
      toolTip: 'Participants',
      icon: <IconUsersOff style={{ fontSize: '80px', right: '-8px', position: 'relative' }} />,
      onClick: props.onParticipantsClick,
    },
    {
      conditionToShow: shouldActivateChatFeature(meeting, userType),
      toolTip: 'Chat',
      icon: (
        <Flex position="relative">
          <IconChatOff style={{ fontSize: '80px' }} />
          {showUnreadNotification && props.amtUnreadMessages !== 0 && (
            <Flex
              position="absolute"
              top={4}
              right={4}
              bg="red"
              borderRadius="50%"
              boxSize={4}
              justifyContent="center"
              alignItems="center"
              color="white"
              fontSize="10px"
              zIndex={5}
            >
              {props.amtUnreadMessages}
            </Flex>
          )}
        </Flex>
      ),
      onClick: props.onChatClick,
    },
    {
      conditionToShow: shouldActivatePIPFeature(meeting, userType),
      toolTip: 'Live Video',
      icon: <IconVideoOff style={{ fontSize: '80px' }} />,
      onClick: props.onVideoClick,
    },
  ];

  // filter elements to dislay only
  const displayElements = elements.filter((elem) => elem.conditionToShow);
  // if no elements to display, just do not render this component
  if (!displayElements.length) return null;

  return (
    <Flex
      position="absolute"
      width={16}
      left={-16}
      bg="white"
      height="fit-content"
      flexDir="column"
      top="50%"
      borderLeftRadius="10px"
      transform="translateY(-50%)"
      boxShadow="white"
      overflow="hidden"
      shadow="sidebar"
    >
      {displayElements.map((elem, idx) => {
        return (
          <Fragment key={elem.toolTip}>
            <Tooltip label={elem.toolTip}>
              <IconButton
                boxSize={16}
                onClick={elem.onClick}
                aria-label={elem.toolTip}
                icon={elem.icon}
                bg="transparent"
                _hover={{ bg: 'transparent' }}
              />
            </Tooltip>
            {idx !== elements.length - 1 && (
              <Flex width="100%" justifyContent="center" alignItems="center">
                <Divider width="80%" />
              </Flex>
            )}
          </Fragment>
        );
      })}
    </Flex>
  );
}
